import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import NorthStarIcon from '../../../../assets/svg/NorthStarIcon';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { anyStopHasAnyWarnings } from '../components/OptimizationWarningsTooltip';
import { useOptimizePlan } from '../hooks/plan-details/useOptimizePlan';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';
import { OptimizationDialog } from '../plan-header/OptimizationDialog';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';

export const PlanOptimizeButton = () => {
  const intl = useIntl();
  const { data: stops } = useRetrieveActivePlanStops();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: drivers } = useRetrieveActivePlanDrivers();

  const { isLoading: optimizingPlan, run: optimizePlan } = useOptimizePlan();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleOptimizePlan = (keepCurrent?: boolean) => {
    if (activePlan) {
      optimizePlan({
        plan: activePlan,
        keepCurrentAssignment: keepCurrent,
      });
    }
  };
  const getOptimizeButtonTooltip = () => {
    if (
      (stops ?? []).length === 0 ||
      (drivers ?? []).length === 0 ||
      (drivers ?? []).filter((route: any) => route.is_locked).length === (drivers ?? []).length
    ) {
      return intl.formatMessage({ id: `planner.plan_details.header.optimize.disabled` });
    }

    if (optimizingPlan) {
      return intl.formatMessage({ id: `planner.plan_details.header.optimize.optimizing` });
    }

    return intl.formatMessage({ id: `planner.plan_details.header.optimize.tooltip` });
  };
  if ((stops ?? []).every((stop: Stop) => stop.driver_id)) {
    return null;
  }

  return (
    <>
      <RoutalTooltip
        id="optimize-button"
        title={
          <Text intlId={getOptimizeButtonTooltip()} variant="body" style={{ cursor: `pointer`, color: `white` }} />
        }
        placement="bottom"
      >
        <Button
          style={{
            backgroundColor: RoutalPalette.primary40,
            borderRadius: `10px`,
            height: `36px`,
            minWidth: `64px`,
            padding: `0`,
            margin: 0,
          }}
          onClick={() => {
            if (
              (stops ?? []).length === 0 ||
              (drivers ?? []).length === 0 ||
              optimizingPlan ||
              (drivers ?? []).filter((route: any) => route.is_locked).length === (drivers ?? []).length
            ) {
              return;
            }
            const requiresConfirmation =
              (stops ?? []).some((stop: Stop) => stop.driver_id) ||
              anyStopHasAnyWarnings({
                stops: stops ?? [],
                drivers: drivers ?? [],
                planGeoFences: projectGeoFences ?? [],
              });
            if (!requiresConfirmation) {
              handleOptimizePlan();
            } else {
              setDialogOpen(true);
            }
          }}
        >
          <div style={{ margin: `0 16px`, height: `24px`, display: `flex`, alignItems: `center` }}>
            {optimizingPlan ? (
              <>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  style={{
                    width: `114px`,
                    height: `16px`,
                    display: `flex`,
                    flexDirection: `row`,
                    alignItems: `center`,
                  }}
                >
                  <div className="lds-ellipsis-little" style={{ height: `8px` }}>
                    <div style={{ top: `2px`, backgroundColor: `white` }} />
                    <div style={{ top: `2px`, backgroundColor: `white` }} />
                    <div style={{ top: `2px`, backgroundColor: `white` }} />
                  </div>
                </Grid>
                <HSpacer small />
              </>
            ) : (
              <Grid container direction="row" wrap="nowrap" textAlign="center" alignItems="center">
                <Text intlId="planner.plan_details.create_routes" style={{ color: `white`, cursor: `pointer` }} />
                <HSpacer small />
                <NorthStarIcon color={RoutalPalette.neutral00} height="13px" width="13px" />
              </Grid>
            )}
          </div>
        </Button>
      </RoutalTooltip>
      <OptimizationDialog
        dialogOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        activePlan={activePlan}
        activeProject={activeProject}
        drivers={drivers}
      />
    </>
  );
};
